import { Cloudinary } from "@cloudinary/base";
import { thumbnail, fill } from "@cloudinary/base/actions/resize";
import { face } from "@cloudinary/base/qualifiers/focusOn";
import { focusOn } from "@cloudinary/base/qualifiers/gravity";

export type ImageSource =
  | string
  | {
      provider?: string | null;
      publicId?: string | null;
      imagekitPath?: string | null;
      renderingBaseUrl?: string | null;
    }
  | null
  | undefined;

export const getImageRenderingKey = (
  image: ImageSource
): string | undefined => {
  if (typeof image === "string") {
    return image;
  }
  if (image?.provider === "imagekit") {
    if (image.renderingBaseUrl) {
      return `imagekit:${image.renderingBaseUrl}`;
    }
    return image.publicId?.startsWith("imagekit:") ? image.publicId : undefined;
  }
  return image?.publicId ?? undefined;
};

export type PersistableImage = {
  publicId?: string | null;
  // TODO(ENG-3436): Remove after migrated images and rollback no longer need Cloudinary metadata.
  sourcePublicId?: string | null;
  provider?: string | null;
  imagekitFileId?: string | null;
  imagekitPath?: string | null;
  renderingBaseUrl?: string | null;
  width: number;
  height: number;
  format: string;
};

export const getImageInput = (
  image: PersistableImage
): Omit<PersistableImage, "renderingBaseUrl"> => ({
  // TODO(ENG-3436): Remove Cloudinary input conversion after old clients and rollback are retired.
  publicId:
    image.provider === "imagekit"
      ? image.sourcePublicId ?? undefined
      : image.publicId?.startsWith("cloudinary:")
      ? image.publicId.slice("cloudinary:".length)
      : image.publicId,
  provider: image.provider ?? undefined,
  imagekitFileId: image.imagekitFileId ?? undefined,
  imagekitPath: image.imagekitPath ?? undefined,
  width: image.width,
  height: image.height,
  format: image.format,
});

const getImageKitImage = (
  baseUrl: string,
  width?: number,
  height?: number,
  faceCrop = false
) => {
  const transforms = [
    width && `w-${width}`,
    height && `h-${height}`,
    width && height && "c-maintain_ratio",
    faceCrop && "fo-face",
  ]
    .filter(Boolean)
    .join(",");
  const url = `${baseUrl}${transforms ? `?tr=${transforms}` : ""}`;
  return { toURL: () => url };
};

const resolveSource = (source: ImageSource) => {
  const key = getImageRenderingKey(source);
  if (key?.startsWith("imagekit:")) {
    return key.startsWith("imagekit:https://")
      ? { provider: "imagekit", value: key.slice("imagekit:".length) }
      : null;
  }
  return key
    ? {
        provider: "cloudinary",
        value: key.startsWith("cloudinary:")
          ? key.slice("cloudinary:".length)
          : key,
      }
    : null;
};

/**
 * Common Cloudinary instance
 */
export const cld = new Cloudinary({
  cloud: {
    cloudName: "whoosh-golf",
  },
});
// TODO(ENG-3436): Remove after ImageKit uploads are enabled for all clients and rollback is retired.
export const cloudinaryUploadPreset = "q8pghodl";
export const cloudinaryCloudName = "whoosh-golf";
export const cloudinaryImgUploadUrl =
  "https://api.cloudinary.com/v1_1/whoosh-golf/image/upload";
/**
 *
 * Ingests cloudinary id. Returns thumbnail focused on face.
 */
export const getFaceThumbnail = (
  id: ImageSource,
  width: number = 140,
  height: number = 140
) => {
  const source = resolveSource(id);
  if (source?.provider === "imagekit") {
    return getImageKitImage(source.value, width, height, true);
  }
  if (source) {
    return cld
      .image(source.value)
      .resize(thumbnail().width(width).height(height).gravity(focusOn(face())));
  }
  return null;
};

// Taken from the reskin branch: this function grabs an image of the specified size set to fill mode.
// From cloudinary docs on Fill Mode: "Creates an image with the exact given width and height without distorting the image.
// This option first scales up or down as much as needed to at least fill both of the given dimensions.
// If the requested aspect ratio is different than the original,
// cropping will occur on the dimension that exceeds the requested size after scaling."
export const getFill = (id?: ImageSource, width?: number, height?: number) => {
  const source = resolveSource(id);
  if (source?.provider === "imagekit" && width && height) {
    return getImageKitImage(source.value, width, height);
  }
  if (source && width && height) {
    return cld.image(source.value).resize(fill().width(width).height(height));
  }
};

export const getThumbnail = (
  id: ImageSource,
  width: number,
  height: number
) => {
  const source = resolveSource(id);
  if (source?.provider === "imagekit") {
    return getImageKitImage(source.value, width, height);
  }
  if (source) {
    return cld
      .image(source.value)
      .resize(thumbnail().width(width).height(height));
  }
  return null;
};
